import { useCallback, useSyncExternalStore } from "react";

/**
 * Subscribes to a CSS media query.
 *
 * This exists because some things cannot be solved by hiding a duplicate with
 * `lg:hidden`. The dashboard view toggle carries an onboarding hint popover
 * anchored to it; rendering the toggle twice and CSS-hiding one copy would
 * leave a popover anchored to a zero-size element, and Radix would position it
 * in the corner. Gating on an actual media query keeps exactly one instance
 * mounted, so the anchor is always the one the user can see.
 *
 * Prefer Tailwind's responsive classes for ordinary show/hide. Reach for this
 * only when the two branches must not coexist in the tree.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );

  const getSnapshot = useCallback((): boolean => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  // Server snapshot: false. The toggle then renders in its mobile slot on the
  // first paint, which is the safer default — a control in the page body is
  // merely lower down, whereas the header slot would overlap the logo.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** Tailwind's `lg` breakpoint — the width at which the sidebar appears. */
export const LG_QUERY = "(min-width: 1024px)";
